import { useMemo } from "react"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { format, parse } from "date-fns"
import type { EngagementTimeEntriesRow } from "@/types/report"

interface TimeEntriesDashboardProps {
  data: EngagementTimeEntriesRow[]
}

export function TimeEntriesDashboard({ data }: TimeEntriesDashboardProps) {
  const chartData = useMemo(() => {
    const monthly = new Map<string, number>()

    for (const row of data) {
      if (!row.isBillable || !row.isApproved || !row.date) continue
      const monthKey = row.date.substring(0, 7) // "yyyy-MM"
      monthly.set(monthKey, (monthly.get(monthKey) ?? 0) + row.hours)
    }

    return Array.from(monthly.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([monthKey, hours]) => ({
        month: format(parse(monthKey, "yyyy-MM", new Date()), "MMM yyyy"),
        hours: Math.round(hours * 100) / 100,
      }))
  }, [data])

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-500">
        No billable hours data available for dashboard.
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Team Billing Hours by Month</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
              <XAxis
                dataKey="month"
                angle={-35}
                textAnchor="end"
                interval={0}
                tick={{ fontSize: 12 }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => [`${value} hrs`, "Billable Hours"]} />
              <Bar dataKey="hours" name="Billable Hours" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  )
}
